import type { RawContentSection } from '@/features/content/useComponent';

import type {
  RawActivity,
  RawCoachGoal,
  RawCoachScoring,
  RawCoachScoringMethod,
  RawFocusArea,
  RawKaiCoachSection,
  RawSession,
} from './kaiCoachTypes';

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Project a rich ContentSection to markdown for display (headings + text + captions). */
export function contentSectionToMarkdown(
  section: RawContentSection | null | undefined,
): string {
  if (!section) {
    return '';
  }
  const units = section.content_units ?? section.contentUnits ?? [];
  const parts: string[] = [];
  for (const unit of units) {
    for (const piece of [unit?.heading, unit?.text, unit?.caption]) {
      const trimmed = str(piece);
      if (trimmed) {
        parts.push(trimmed);
      }
    }
  }
  return parts.join('\n\n');
}

function parseMillis(value: unknown): number | null {
  const trimmed = str(value);
  if (!trimmed) {
    return null;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

// A student-selectable focus area on a thread. Only the uid (sent to the server)
// and student-visible title are surfaced; the instructor's hidden instructions
// stay server-authoritative and are never needed by the student view.
export interface CoachThreadFocusArea {
  title: string;
  uid: string;
}

export interface CoachThread {
  activityUid: string;
  // The due date that governs this thread: the activity's own when it has one,
  // otherwise its session's. null means the thread never closes.
  dueAtMillis: number | null;
  // Instructor-authored focus areas for this thread (empty when none).
  focusAreas: CoachThreadFocusArea[];
  // Goal-reached credit: whether the unit has a goal Kai can judge, and the
  // line students are shown ('' when there is none) — each resolved against the
  // coach's defaults, like points.
  goalSummary: string;
  hasGoal: boolean;
  // Stable key for selection/rendering.
  key: string;
  label: string;
  openAtMillis: number | null;
  // Points this thread is worth (0 when none). Under goal-reached credit a unit
  // without its own points is worth the coach's default.
  points: number;
  promptMarkdown: string;
  // Messages the student must send to earn the points (0 when unset).
  requiredInteractions: number;
  // The uid this thread's score is stored under in section_stats: the activity
  // uid when the session is split, otherwise the session uid.
  scoreUnitUid: string;
  sessionLabel: string;
  sessionUid: string;
}

export type CoachCreditMethod = 'participation' | 'goalReached';

export interface NormalizedCoachSection {
  activityDimensionLabel: string;
  // True when the instructor turned on credit.
  creditEnabled: boolean;
  // How credit is earned when it is on.
  creditMethod: CoachCreditMethod;
  orientationMarkdown: string;
  sessionDimensionLabel: string;
  threads: CoachThread[];
}

function parseNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const trimmed = str(value);
  if (!trimmed) {
    return 0;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 0;
}

// Surface only titled focus areas (an untitled one has no dropdown label). The
// uid falls back to the array position so selection still resolves server-side.
function parseFocusAreas(
  raw: RawFocusArea[] | null | undefined,
): CoachThreadFocusArea[] {
  return (raw ?? [])
    .map((area, index) => ({
      title: str(area.title),
      uid: str(area.uid) || `focus-${index}`,
    }))
    .filter((area) => area.title !== '');
}

export function parseCreditMethod(method: RawCoachScoringMethod | undefined): CoachCreditMethod {
  return method === 'GOAL_REACHED' || method === 1 ? 'goalReached' : 'participation';
}

// A present, finite number; null when the field is absent or blank. Points use
// it because presence matters: a unit's own 0 takes it out of credit, while an
// absent value falls back to the coach's default.
function parseOptionalNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = str(value);
  if (!trimmed) {
    return null;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function goalSummaryOf(goal: RawCoachGoal | null | undefined): string {
  return str(goal?.student_summary ?? goal?.studentSummary);
}

function goalHasCriteria(goal: RawCoachGoal | null | undefined): boolean {
  return (goal?.criteria ?? []).some((criterion) => str(criterion?.text) !== '');
}

// The coach-level defaults a unit falls back to. Goal-reached credit only;
// under participation a unit has only what it sets itself (mirrors the
// backend's coachUnitPoints / coachResolvedGoal).
interface UnitDefaults {
  goal: RawCoachGoal | null;
  points: number;
}

function unitDefaults(scoring: RawCoachScoring | null | undefined): UnitDefaults | null {
  if (parseCreditMethod(scoring?.method) !== 'goalReached') {
    return null;
  }
  return {
    goal: scoring?.default_goal ?? scoring?.defaultGoal ?? null,
    points: parseOptionalNumber(scoring?.default_points ?? scoring?.defaultPoints) ?? 0,
  };
}

// A unit's points and goal as the student view needs them, each value resolved
// on its own: the unit's own points when present, its own criteria when any is
// non-empty, its own summary when not blank — otherwise the default's.
function resolveUnit(
  ownPoints: unknown,
  ownGoal: RawCoachGoal | null | undefined,
  defaults: UnitDefaults | null,
): Pick<CoachThread, 'goalSummary' | 'hasGoal' | 'points'> {
  return {
    goalSummary: goalSummaryOf(ownGoal) || (defaults ? goalSummaryOf(defaults.goal) : ''),
    hasGoal: goalHasCriteria(ownGoal) || (defaults ? goalHasCriteria(defaults.goal) : false),
    points: parseOptionalNumber(ownPoints) ?? defaults?.points ?? 0,
  };
}

function sessionPrompt(session: RawSession): string {
  return contentSectionToMarkdown(
    session.assignment_prompt ?? session.assignmentPrompt,
  );
}

function activityPrompt(activity: RawActivity, fallback: string): string {
  return (
    contentSectionToMarkdown(
      activity.assignment_prompt ?? activity.assignmentPrompt,
    ) || fallback
  );
}

/**
 * Flatten a coach section into the ordered list of student-selectable threads:
 * one per activity where a session is split, otherwise one per session. Locked
 * sessions still appear (with their open date) so students see what's coming.
 */
export function normalizeCoachSection(
  section: RawKaiCoachSection | null | undefined,
): NormalizedCoachSection {
  const threads: CoachThread[] = [];
  const sessions = section?.sessions ?? [];
  const defaults = unitDefaults(section?.scoring);

  sessions.forEach((session, sessionIndex) => {
    const sessionUid = str(session.uid) || `session-${sessionIndex}`;
    const sessionLabel = str(session.label) || `Session ${sessionIndex + 1}`;
    const openAtMillis = parseMillis(
      session.open_at_utc_milli ?? session.openAtUtcMilli,
    );
    const sessionDueAtMillis = parseMillis(
      session.due_at_utc_milli ?? session.dueAtUtcMilli,
    );
    const prompt = sessionPrompt(session);
    const activities = session.activities ?? [];

    if (activities.length === 0) {
      threads.push({
        activityUid: '',
        dueAtMillis: sessionDueAtMillis,
        focusAreas: parseFocusAreas(session.focus_areas ?? session.focusAreas),
        ...resolveUnit(session.points, session.goal, defaults),
        key: sessionUid,
        label: sessionLabel,
        openAtMillis,
        promptMarkdown: prompt,
        requiredInteractions: parseNumber(
          session.required_interactions ?? session.requiredInteractions,
        ),
        scoreUnitUid: sessionUid,
        sessionLabel,
        sessionUid,
      });
      return;
    }

    activities.forEach((activity, activityIndex) => {
      const activityUid = str(activity.uid) || `activity-${activityIndex}`;
      threads.push({
        activityUid,
        dueAtMillis:
          parseMillis(activity.due_at_utc_milli ?? activity.dueAtUtcMilli) ??
          sessionDueAtMillis,
        focusAreas: parseFocusAreas(activity.focus_areas ?? activity.focusAreas),
        ...resolveUnit(activity.points, activity.goal, defaults),
        key: `${sessionUid}/${activityUid}`,
        label: str(activity.label) || `Activity ${activityIndex + 1}`,
        openAtMillis,
        promptMarkdown: activityPrompt(activity, prompt),
        requiredInteractions: parseNumber(
          activity.required_interactions ?? activity.requiredInteractions,
        ),
        scoreUnitUid: activityUid,
        sessionLabel,
        sessionUid,
      });
    });
  });

  return {
    activityDimensionLabel:
      str(section?.activity_dimension_label ?? section?.activityDimensionLabel) ||
      'Activity',
    creditEnabled: section?.scoring?.enabled === true,
    creditMethod: parseCreditMethod(section?.scoring?.method),
    orientationMarkdown: contentSectionToMarkdown(
      section?.student_orientation ?? section?.studentOrientation,
    ),
    sessionDimensionLabel:
      str(section?.session_dimension_label ?? section?.sessionDimensionLabel) ||
      'Session',
    threads,
  };
}

/** A thread is enterable when its session has no open date or it has passed. */
export function isThreadOpen(thread: CoachThread, now: number): boolean {
  return thread.openAtMillis === null || thread.openAtMillis <= now;
}

/**
 * A thread is closed once its due date has passed. A thread with no due date
 * never closes. Closing is independent of opening: a closed thread was open,
 * so it still counts toward the participation maximum and can still be read.
 */
export function isThreadClosed(thread: CoachThread, now: number): boolean {
  return thread.dueAtMillis !== null && thread.dueAtMillis <= now;
}

/**
 * Students may send to a thread that has opened and has not closed. Mirrors
 * the server's coachThreadAcceptsMessagesAt, which is what actually decides.
 */
export function threadAcceptsMessages(thread: CoachThread, now: number): boolean {
  return isThreadOpen(thread, now) && !isThreadClosed(thread, now);
}

// One scorable unit a due entry covers, for telling whether a student has
// completed it.
export interface CoachDueEntryUnit {
  points: number;
  uid: string;
}

// One of a coach's deadlines: a session, or the activities of a split session
// that share a due date.
export interface CoachDueEntry {
  dueAtMillis: number;
  // Stable across renders: the session uid and the due instant.
  key: string;
  // "Week 3", "Week 3 · Peer response", or "Week 3 · Draft, Revision".
  label: string;
  openAtMillis: number | null;
  sessionUid: string;
  // The threads the entry covers, in authored order; a link opens the first.
  threadKeys: string[];
  units: CoachDueEntryUnit[];
}

/**
 * A coach's deadlines, one entry per session and effective due date. A session
 * that is not split — or a split session whose activities all share its due
 * date — is one entry named after the session. An activity with its own due
 * date is its own entry, "Session · Activity" ("Session · A, B" when several
 * share one). Threads with no due date have no entry. Ordered by due date, then
 * by session order.
 */
export function coachDueEntries(threads: CoachThread[]): CoachDueEntry[] {
  const threadsPerSession = new Map<string, number>();
  for (const thread of threads) {
    threadsPerSession.set(thread.sessionUid, (threadsPerSession.get(thread.sessionUid) ?? 0) + 1);
  }

  const groups = new Map<string, CoachThread[]>();
  for (const thread of threads) {
    if (thread.dueAtMillis === null) {
      continue;
    }
    const key = `${thread.sessionUid}@${thread.dueAtMillis}`;
    groups.set(key, [...(groups.get(key) ?? []), thread]);
  }

  return [...groups.entries()]
    .map(([key, members], index) => {
      const [first] = members;
      const wholeSession = members.length === threadsPerSession.get(first.sessionUid);
      return {
        entry: {
          dueAtMillis: first.dueAtMillis as number,
          key,
          label:
            wholeSession || !first.activityUid
              ? first.sessionLabel
              : `${first.sessionLabel} · ${members.map((member) => member.label).join(', ')}`,
          openAtMillis: first.openAtMillis,
          sessionUid: first.sessionUid,
          threadKeys: members.map((member) => member.key),
          units: members.map((member) => ({ points: member.points, uid: member.scoreUnitUid })),
        },
        index,
      };
    })
    .sort((a, b) => a.entry.dueAtMillis - b.entry.dueAtMillis || a.index - b.index)
    .map(({ entry }) => entry);
}

/**
 * A student has completed a due entry once they have earned every point of
 * every unit it covers. A unit without points can never be completed, so an
 * entry on a coach without credit stays due until its due date, like homework.
 */
export function isCoachDueEntryComplete(
  entry: Pick<CoachDueEntry, 'units'>,
  earned: Record<string, number>,
): boolean {
  return (
    entry.units.length > 0 &&
    entry.units.every((unit) => unit.points > 0 && (earned[unit.uid] ?? 0) >= unit.points)
  );
}

// Minimal shape of a stored section stat (the same entity regular assignments
// use). Coach participation scores are stored one per scorable unit, keyed by
// the unit uid, with the earned points in total_score.
export interface RawSectionStatLike {
  id?: string | number | null;
  total_score?: number | string | null;
  totalScore?: number | string | null;
}

/** Build a unitUid → earned-points map from a student component's section_stats. */
export function earnedByUnit(
  stats: RawSectionStatLike[] | null | undefined,
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const stat of stats ?? []) {
    const id = stat?.id != null ? String(stat.id).trim() : '';
    if (id) {
      map[id] = parseNumber(stat?.total_score ?? stat?.totalScore);
    }
  }
  return map;
}

/**
 * The maximum participation score a student can currently earn: the sum of
 * points over every open thread. Matches the backend's coachVisibleMaxScore.
 */
export function coachVisibleMaxScore(
  threads: CoachThread[],
  now: number,
): number {
  return threads.reduce(
    (sum, thread) => (isThreadOpen(thread, now) ? sum + thread.points : sum),
    0,
  );
}
